import { useCallback } from 'react';

import { IStoreRedirect, IStoreRedirectDeps, IStoreRedirectOptions } from './useStoreRedirect.types';
import { performStoreRedirect } from './useStoreRedirect.utils';

/**
 * Redirects to the web store, forwarding a one-time login token so the
 * destination store auto-logs-in the current player. App-specific
 * dependencies (token minting, login state, storage) are injected so the
 * hook stays framework- and app-agnostic.
 *
 * @example
 * const { redirect } = useStoreRedirect({
 *   getRedirectToken: async () => (await mutateAsync()).data.token,
 *   isLoggedIn,
 *   setAutoCollectFlag: () => webStorageUtil.set(ELocalStorageKeys.AUTO_COLLECT_FLAG, 'true'),
 * });
 * await redirect(redirectUrl, isStoreRedirect, { autoCollectOfferId });
 */
export const useStoreRedirect = ({
    createJourneyId,
    getRedirectToken,
    isLoggedIn,
    now,
    onRedirectEvent,
    setAutoCollectFlag,
}: IStoreRedirectDeps): IStoreRedirect => {
    const redirect = useCallback(
        (redirectUrl?: string, isStoreRedirect?: boolean, options?: IStoreRedirectOptions) =>
            performStoreRedirect(
                {
                    createJourneyId,
                    getRedirectToken,
                    isLoggedIn,
                    now,
                    onRedirectEvent,
                    setAutoCollectFlag,
                },
                redirectUrl,
                isStoreRedirect,
                options,
            ),
        [
            createJourneyId,
            getRedirectToken,
            isLoggedIn,
            now,
            onRedirectEvent,
            setAutoCollectFlag,
        ],
    );

    return { redirect };
};
